import type { BilanPage, SessionBilan, SessionGoal, SessionNotes } from "@tutor/protocol";
import { lazy, Suspense, useEffect, useId, useState } from "react";
import { useLocale, useT } from "../i18n";
import { stageWords } from "../insights/stages";
import { CHOICE_KEYS } from "../lesson/ItemCard";
import { PromptText, promptSize } from "../lesson/PromptText";
import * as api from "./api";
import { errorKey } from "./errors";
import { skillLabel } from "./skill-label";
import { parentPlural, useParentT, type ParentStringKey } from "./strings";

// The bilan of each session (ADR-0055): the engine's plan with its ticks, the session's
// figures and the steps it climbed, then its board pages on request, read-only. Every
// figure comes from the API, which reads the engine's outputs in the child's log; this
// only words them. Parents keep numbers (ADR-0043).

const WidgetView = lazy(() =>
  import("../widgets/WidgetView").then((module) => ({ default: module.WidgetView })),
);

/** How long to wait before looking again for lines still being written. */
export const NOTES_RETRY_MS = 6_000;

export function Sessions(props: {
  profileId: string;
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { profileId, onAuthError } = props;
  const t = useParentT();
  const locale = useLocale();
  const [sessions, setSessions] = useState<readonly SessionBilan[] | null>(null);
  const [error, setError] = useState<ParentStringKey>();

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setSessions(null);
    const load = (again: boolean) => {
      void api.getSessions(profileId, locale, 7).then((res) => {
        if (!live) return;
        if (res.ok) {
          setSessions(res.value.sessions);
          setError(undefined);
          // Lines are being written for a finished session: one more look shortly.
          if (res.value.notesPending && again) {
            timer = setTimeout(() => {
              load(false);
            }, NOTES_RETRY_MS);
          }
        } else if (!onAuthError(res.error)) setError(errorKey(res.error));
      });
    };
    load(true);
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [profileId, locale, onAuthError]);

  let body;
  if (error !== undefined) {
    body = (
      <p className="parent-message" role="alert">
        {t(error)}
      </p>
    );
  } else if (sessions === null) {
    body = <p className="parent-muted">{t("parent.loading")}</p>;
  } else if (sessions.length === 0) {
    body = <p className="parent-muted">{t("sessions.empty")}</p>;
  } else {
    body = (
      <ol className="pm-sessions">
        {sessions.map((session) => (
          <SessionCard
            key={session.sessionStart}
            profileId={profileId}
            session={session}
            onAuthError={onAuthError}
          />
        ))}
      </ol>
    );
  }
  return (
    <section className="pm-sessions-section" aria-labelledby="pm-sessions-title">
      <h3 id="pm-sessions-title" className="pm-group__title">
        {t("sessions.title")}
      </h3>
      {body}
    </section>
  );
}

function SessionCard(props: {
  profileId: string;
  session: SessionBilan;
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { profileId, session, onAuthError } = props;
  const t = useParentT();
  const child = useT();
  const locale = useLocale();
  const titleId = useId();
  const pagesId = useId();
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<readonly BilanPage[] | null>(null);
  const [error, setError] = useState<ParentStringKey>();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next || pages !== null) return;
    setError(undefined);
    void api.getSessionPages(profileId, session.sessionStart).then((res) => {
      if (res.ok) setPages(res.value.pages);
      else if (!onAuthError(res.error)) setError(errorKey(res.error));
    });
  };

  const started = new Date(session.sessionStart * 60_000);
  const when = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(started);
  const figures = [parentPlural(locale, "sessions.exercises", session.exercises)];
  if (session.exercises > 0) figures.push(t("sessions.firstTry", { count: session.firstTry }));
  if (session.withHints > 0) figures.push(t("sessions.withHints", { count: session.withHints }));

  return (
    <li className="pm-session" aria-labelledby={titleId} data-session={session.sessionStart}>
      <header className="pm-session__head">
        <h4 id={titleId} className="pm-session__when">
          <time dateTime={started.toISOString()}>{upperFirst(when)}</time>
        </h4>
        {session.minutes == null ? null : (
          <span className="pm-session__minutes">
            {t("sessions.minutes", { count: session.minutes })}
          </span>
        )}
      </header>

      {session.notes == null ? null : <NotesView notes={session.notes} />}

      {session.goals.length > 0 ? (
        <div className="pm-session__plan">
          <p className="pm-session__label">{t("sessions.plan")}</p>
          <ul className="pm-session__goals">
            {session.goals.map((goal) => (
              <li key={goal.skillId} className="pm-session__goal" data-state={goal.state}>
                <span className="pm-session__tick" aria-hidden="true">
                  {goal.state === "done" ? "✓" : "·"}
                </span>
                <span>
                  {goalLabel(t, goal, skillLabel(goal.skillId, locale))}
                  <span className="pm-session__state">
                    {" "}
                    · {t(goal.state === "done" ? "sessions.goal.done" : "sessions.goal.later")}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="pm-session__figures">{figures.join(", ")}</p>

      {session.climbs.length > 0 ? (
        <div className="pm-session__climbs">
          <p className="pm-session__label">{t("sessions.climbs")}</p>
          <ul>
            {session.climbs.map((climb) => {
              const name = skillLabel(climb.skillId, locale);
              const words = stageWords(child, name, climb.toStep, climb.steps);
              const gained = climb.toStep - climb.fromStep;
              return (
                <li key={climb.skillId} data-climb={climb.skillId}>
                  <span className="pm-climb__name">{name}</span> · {words.where}
                  {gained > 0 ? ` · ${parentPlural(locale, "sessions.steps", gained)}` : ""}
                  {climb.mastered ? ` · ${t("sessions.mastered")}` : ""}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {session.exercises > 0 ? (
        <button
          type="button"
          className="parent-link"
          aria-expanded={open}
          aria-controls={pagesId}
          onClick={toggle}
        >
          {t(open ? "sessions.closePages" : "sessions.openPages")}
        </button>
      ) : (
        <p className="parent-muted">{t("sessions.noPages")}</p>
      )}

      {open ? (
        <div id={pagesId} className="pm-session__pages">
          {error !== undefined ? (
            <p className="parent-message" role="alert">
              {t(error)}
            </p>
          ) : pages === null ? (
            <p className="parent-muted">{t("parent.loading")}</p>
          ) : (
            <>
              <p className="parent-muted">{t("sessions.pagesNote")}</p>
              <ol className="pm-pages">
                {pages.map((page, i) => (
                  <PageView key={`${page.itemId}-${String(i)}`} page={page} n={i + 1} />
                ))}
              </ol>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** The session's three written lines (ADR-0055), each only when written. */
function NotesView(props: { notes: SessionNotes }) {
  const t = useParentT();
  const lines = (
    [
      ["sessions.notes.strong", props.notes.strong],
      ["sessions.notes.watch", props.notes.watch],
      ["sessions.notes.question", props.notes.question],
    ] as const
  ).flatMap(([label, text]) => (text == null ? [] : [{ label, text }]));
  if (lines.length === 0) return null;
  return (
    <div className="pm-session__notes">
      <dl>
        {lines.map(({ label, text }) => (
          <div key={label} className="pm-session__note" data-note={label}>
            <dt>{t(label)}</dt>
            <dd>{text}</dd>
          </div>
        ))}
      </dl>
      <p className="parent-muted">{t("sessions.notes.source")}</p>
    </div>
  );
}

/** A finished exercise as the board showed it, read-only, with each try's grade. */
function PageView(props: { page: BilanPage; n: number }) {
  const { page, n } = props;
  const t = useParentT();
  const promptId = useId();
  const solution = page.solution ?? null;
  const bench = page.bench ?? null;
  const hint = page.hint ?? null;
  return (
    <li className="pm-page" aria-labelledby={promptId} data-item-id={page.itemId}>
      <p className="pm-page__kicker">{t("sessions.page", { n })}</p>
      <p id={promptId} className="pm-page__prompt" data-size={promptSize(page.prompt)}>
        <PromptText text={page.prompt} />
      </p>
      {page.answer.kind === "choice" ? (
        <ol className="pm-page__choices">
          {page.answer.choices.map((choice, i) => (
            <li key={choice}>
              {CHOICE_KEYS.charAt(i)}. {choice}
            </li>
          ))}
        </ol>
      ) : null}
      <ol className="pm-page__tries">
        {page.tries.map((right, i) => (
          <li key={i} data-right={right}>
            {t("sessions.try", {
              n: i + 1,
              verdict: t(right ? "sessions.tryRight" : "sessions.tryNotYet"),
            })}
          </li>
        ))}
      </ol>
      {solution === null ? null : (
        <div className="pm-page__solution">
          <p>{t("sessions.rightAnswer", { answer: shownAnswer(page, solution.answer) })}</p>
          <p>{solution.explanation}</p>
        </div>
      )}
      {hint === null ? null : (
        <p className="parent-muted">{t("sessions.hint", { level: t(`sessions.hint.${hint}`) })}</p>
      )}
      {bench === null ? null : (
        <div className="workbench pm-page__bench">
          <Suspense fallback={<div className="workbench-loading" aria-busy="true" />}>
            <WidgetView key={bench.source} block={bench} />
          </Suspense>
        </div>
      )}
    </li>
  );
}

function goalLabel(t: ReturnType<typeof useParentT>, goal: SessionGoal, skill: string): string {
  return t(goal.kind === "review" ? "sessions.goal.review" : "sessions.goal.climb", { skill });
}

/** The expected answer as the child would see it: the choice's letter and text. */
function shownAnswer(page: BilanPage, answer: string): string {
  if (page.answer.kind !== "choice") return answer;
  const index = Number(answer);
  const choice = page.answer.choices[index];
  return choice === undefined ? answer : `${CHOICE_KEYS.charAt(index)}. ${choice}`;
}

function upperFirst(text: string): string {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}
